import { useQueryClient } from '@tanstack/react-query'
import { useAblyChannel } from './useAblyChannel.js'
import { DEFAULT_META } from '../data/metaApi.js'
import { rememberAuthoritative } from '../data/weeklySummaryOverrides.js'

/**
 * useWeeklySummarySync — subscribes to `weekly-summary-updated` Ably
 * events and applies each update directly to the React Query cache for
 * meta.weeklySummary[goalKey]. No refetch involved, so there's no KV
 * propagation delay and no risk of a stale refetch wiping a recent
 * save.
 *
 * When a teammate blurs out of a weekly summary field, everyone else
 * sees the new value in real time (~100-200ms via Ably), without
 * needing to refresh.
 *
 * Mount once at the app root.
 */
export function useWeeklySummarySync() {
  const queryClient = useQueryClient()

  useAblyChannel('weekly-summary-updated', (data) => {
    if (!data?.goalKey || !data?.entry) return
    const { goalKey, entry } = data
    queryClient.setQueryData(['meta'], (prev) => {
      const meta = prev || DEFAULT_META
      return {
        ...meta,
        weeklySummary: {
          ...(meta.weeklySummary || {}),
          [goalKey]: entry,
        },
      }
    })
    // Also pin as authoritative for ~15s so any stale refetch that sneaks
    // in (e.g. triggered by another unrelated meta-changed event) can't
    // roll this entry back.
    rememberAuthoritative(goalKey, entry)
  })
}
